import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  formatCompactMoney,
  formatThousands,
  sparklinePoints,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

/** Tamanho do desenho (viewBox; o SVG estica na largura do KPI). */
const SPARK_WIDTH = 160;
const SPARK_HEIGHT = 32;
/** Até quantos anos a sparkline mostra o valor de cada ponto. */
const MAX_VALUE_LABELS = 4;

/**
 * Linha simples de evolução (sparkline) para os KPIs de patrimônio do sócio e do grupo. Só desenha os valores que
 * vieram do backend; com menos de 2 anos com valor, não aparece.
 */
@Component({
  selector: 'app-pit-sparkline',
  standalone: true,
  template: `
    @if (spark(); as line) {
      <figure class="spark" [attr.aria-label]="line.label" role="img">
        <!-- Área do desenho: ponto e rótulos posicionados em % dela, não da figura (que inclui os anos). -->
        <div class="spark__plot">
          <svg
            class="spark__svg"
            [attr.viewBox]="'0 0 ' + width + ' ' + height"
            preserveAspectRatio="none"
            aria-hidden="true">
            <path class="spark__line" [attr.d]="line.path" />
          </svg>
          <!-- O ponto final fica fora do SVG esticado, para continuar redondo. -->
          <span
            class="spark__dot"
            [style.left.%]="(line.last.x / width) * 100"
            [style.top.%]="(line.last.y / height) * 100"></span>
          <!-- Valor de cada ano em R$ mil, acima do ponto (só o primeiro e o último com mais de 4 anos) (R4-5). -->
          @for (point of line.labels; track point.year; let first = $first; let last = $last) {
            <span
              class="spark__value"
              [class.spark__value--first]="first"
              [class.spark__value--last]="last"
              [class.spark__value--current]="last"
              [style.left.%]="(point.x / width) * 100"
              [style.top.%]="(point.y / height) * 100"
              >{{ point.text }}</span
            >
          }
        </div>
        <figcaption class="spark__years">
          <span>{{ line.firstYear }}</span>
          <span>{{ line.lastYear }}</span>
        </figcaption>
      </figure>
    }
  `,
  styleUrl: './sparkline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SparklineComponent {
  readonly series = input<{ year: number; value: number | null }[]>([]);
  /** Começo do texto lido pelo leitor de tela: "Patrimônio líquido por ano". */
  readonly label = input('Patrimônio líquido por ano');

  protected readonly width = SPARK_WIDTH;
  protected readonly height = SPARK_HEIGHT;

  protected readonly spark = computed(() => {
    const series = this.series();
    const points = sparklinePoints(series, SPARK_WIDTH, SPARK_HEIGHT);
    if (!points) return null;
    const known = series.filter(item => item.value != null);
    const all = points.points;
    // Até 4 anos cabem todos os rótulos sem poluir; acima disso, só o primeiro e o último.
    const shown = all.length <= MAX_VALUE_LABELS ? all : [all[0], all[all.length - 1]];
    return {
      ...points,
      firstYear: known[0].year,
      lastYear: known[known.length - 1].year,
      labels: shown.map(point => ({ ...point, text: formatThousands(point.value) })),
      label: `${this.label()}. ${known.map(item => `${item.year}: ${formatCompactMoney(item.value)}`).join('; ')}`,
    };
  });
}
